import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import {
    collegePicked,
    flowReset,
    fmCountryPicked,
    fmDisciplinePicked,
    genderPicked,
    imDisciplinePicked,
    imInstitutionPicked,
    resultReady,
    selectionsHealed,
    specialtyPicked,
    stepChanged,
    trackChanged,
    universityPicked,
    type DreamStepId,
} from "@/redux/slices/dreamUniversitySlice";
import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { College, Department, Gender, Track } from "./data/sharedTypes";
import {
    type UniversityId,
    getColleges,
    getRequiredFields,
    hasSelectableSpecialties,
    requiresGenderSelection,
    universities,
} from "./data/universityRegistry";
import { type FmCountry, type FmDiscipline } from "./data/foreignMissionsData";
import { type ImInstitution, type ImDiscipline } from "./data/internalMissionsData";
import { type Scores } from "./utils/calculateEquivalentRate";
import StepIndicator from "./StepIndicator";
import WhatsAppContact from "./components/WhatsAppContact";
import ChooseUniversity from "./steps/ChooseUniversity";
import ChooseGender from "./steps/ChooseGender";
import ChooseCollege from "./steps/ChooseCollege";
import ChooseFmCountry from "./steps/ChooseFmCountry";
import ChooseFmCollege from "./steps/ChooseFmCollege";
import ChooseFmSpecialty from "./steps/ChooseFmSpecialty";
import ChooseImInstitution from "./steps/ChooseImInstitution";
import ChooseImDiscipline from "./steps/ChooseImDiscipline";
import ChooseImSpecialty from "./steps/ChooseImSpecialty";
import ChooseSpecialty from "./steps/ChooseSpecialty";
import PaaetDocs from "./steps/PaaetDocs";
import ScoreCalculator from "./steps/ScoreCalculator";
import ResultsView from "./steps/ResultsView";
import QualifiedCollegesModal from "./steps/QualifiedCollegesModal";

type StepId = DreamStepId;

interface StepDef {
    key: StepId;
    label: string;
}

const BASE_STEPS: StepDef[] = [
    { key: "university", label: "dream_university_step_university" },
    { key: "college", label: "dream_university_step_college" },
    { key: "specialty", label: "dream_university_step_specialty" },
    { key: "scores", label: "dream_university_step_scores" },
    { key: "results", label: "dream_university_step_results" },
];

const GENDER_STEP: StepDef = { key: "gender", label: "dream_university_step_gender" };
const PAAET_DOCS_STEP: StepDef = { key: "paaet_docs", label: "dream_university_step_paaet_docs" };
const FM_COUNTRY_STEP: StepDef = { key: "fm_country", label: "dream_university_fm_step_country" };
const FM_COLLEGE_STEP: StepDef = { key: "fm_college", label: "dream_university_fm_step_college" };
const IM_INSTITUTION_STEP: StepDef = { key: "im_institution", label: "dream_university_im_step_institution" };
const IM_DISCIPLINE_STEP: StepDef = { key: "im_discipline", label: "dream_university_im_step_discipline" };

const stepVariants = {
    enter: { opacity: 0, x: 20 },
    center: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -20 },
};

export default function DreamUniversitySection() {
    const dispatch = useAppDispatch();
    // The whole wizard journey (selections, step, entered numbers) lives in a
    // persisted redux slice so a returning student continues where they left
    // off. Resets are encoded in the slice: a different university wipes
    // everything; a specialty with a different number of score inputs wipes
    // only the numbers.
    const {
        activeStepId,
        universityId: selectedUniversityId,
        gender: selectedGender,
        college: selectedCollege,
        department: selectedDepartment,
        track: selectedTrack,
        fmCountry,
        fmDiscipline,
        imInstitution,
        imDiscipline,
        resultScores,
        equivalentRate,
    } = useAppSelector((state) => state.dreamUniversity);
    const scores: Scores = resultScores ?? { high_school: 0 };
    const [showQualifiedModal, setShowQualifiedModal] = useState(false);
    const sectionRef = useRef<HTMLDivElement>(null);

    const scrollToTop = useCallback(() => {
        sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, []);

    const isFm = selectedUniversityId === "foreign_missions";
    const isIm = selectedUniversityId === "internal_missions";
    const isPaaet = selectedUniversityId === "paaet";

    /**
     * Rehydration guard (runs once). The persisted college/department are
     * snapshots; rates data may have been updated since the student's last
     * visit. Re-resolve both from the current data modules by key so the
     * calculator always uses fresh weights, and restart the flow if the
     * persisted selection no longer exists or the saved step lost the
     * selections it needs.
     */
    useEffect(() => {
        if (!selectedUniversityId) {
            return;
        }
        if (selectedCollege) {
            const freshCollege = getColleges(selectedUniversityId, selectedGender ?? undefined)
                .find((c) => c.key === selectedCollege.key) ?? null;
            const freshDepartment = freshCollege && selectedDepartment
                ? freshCollege.departments.find(
                    (d) =>
                        d.code === selectedDepartment.code &&
                        d.departmentKey === selectedDepartment.departmentKey &&
                        (d.track ?? null) === (selectedDepartment.track ?? null) &&
                        (d.gender ?? null) === (selectedDepartment.gender ?? null) &&
                        (d.stage ?? null) === (selectedDepartment.stage ?? null),
                ) ?? null
                : null;
            if (!freshCollege || (selectedDepartment && !freshDepartment)) {
                dispatch(flowReset());
                return;
            }
            dispatch(selectionsHealed({ college: freshCollege, department: freshDepartment }));
        }
        const stepIsValid: Record<StepId, boolean> = {
            university: true,
            gender: true,
            paaet_docs: selectedGender !== null,
            fm_country: isFm,
            fm_college: isFm && fmCountry !== null,
            im_institution: isIm,
            im_discipline: isIm && imInstitution !== null,
            im_specialty: isIm && imInstitution !== null && imDiscipline !== null,
            college: !isFm && !isIm,
            specialty: isFm
                ? fmCountry !== null && fmDiscipline !== null
                : !isIm && selectedCollege !== null,
            scores: selectedCollege !== null,
            results: selectedCollege !== null && resultScores !== null,
        };
        if (!stepIsValid[activeStepId as StepId]) {
            dispatch(stepChanged("university"));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const needsGender = selectedUniversityId
        ? requiresGenderSelection(selectedUniversityId)
        : false;

    const collegeHasSpecialties = selectedCollege && selectedUniversityId
        ? hasSelectableSpecialties(selectedUniversityId, selectedCollege.key, selectedGender ?? undefined, selectedTrack)
        : false;

    const steps = useMemo(() => {
        // PAAET (temporary): only University → Gender → Rates documents.
        // The original college/specialty/scores/results flow is hidden but
        // kept in the codebase for later re-enabling.
        if (isPaaet) {
            return [BASE_STEPS[0], GENDER_STEP, PAAET_DOCS_STEP];
        }
        let list = [...BASE_STEPS];
        if (needsGender) {
            list.splice(1, 0, GENDER_STEP);
        }
        if (isFm) {
            list = list.filter((s) => s.key !== "college");
            const universityIdx = list.findIndex((s) => s.key === "university");
            list.splice(universityIdx + 1, 0, FM_COUNTRY_STEP, FM_COLLEGE_STEP);
        }
        if (isIm) {
            // Internal Missions: replace "college" with Institution + Discipline
            // steps (im_institution → im_discipline → specialty).
            list = list.filter((s) => s.key !== "college");
            const universityIdx = list.findIndex((s) => s.key === "university");
            list.splice(universityIdx + 1, 0, IM_INSTITUTION_STEP, IM_DISCIPLINE_STEP);
        }
        if (!collegeHasSpecialties && !isFm && !isIm) {
            list = list.filter((s) => s.key !== "specialty");
        }
        return list;
    }, [needsGender, collegeHasSpecialties, isFm, isIm, isPaaet]);

    const currentStepIndex =
        steps.findIndex((s) => s.key === activeStepId) + 1;

    const goTo = (stepId: StepId) => {
        dispatch(stepChanged(stepId));
        setTimeout(scrollToTop, 50);
    };

    const universityInfo = selectedUniversityId
        ? universities.find((u) => u.id === selectedUniversityId)
        : null;

    const handleSelectUniversity = (universityId: UniversityId) => {
        dispatch(universityPicked(universityId));
        if (requiresGenderSelection(universityId)) {
            goTo("gender");
        } else if (universityId === "foreign_missions") {
            goTo("fm_country");
        } else if (universityId === "internal_missions") {
            goTo("im_institution");
        } else {
            goTo("college");
        }
    };

    const handleSelectGender = (gender: Gender) => {
        dispatch(genderPicked(gender));
        // PAAET (temporary): show the rates documents instead of the
        // college flow. Other gender-gated sections still go to "college".
        goTo(selectedUniversityId === "paaet" ? "paaet_docs" : "college");
    };

    const handleSelectFmCountry = (country: FmCountry, track: Track) => {
        dispatch(fmCountryPicked({ country, track }));
        goTo("fm_college");
    };

    const handleSelectFmDiscipline = (discipline: FmDiscipline) => {
        dispatch(fmDisciplinePicked(discipline));
        goTo("specialty");
    };

    const handleSelectImInstitution = (institution: ImInstitution, track: Track) => {
        dispatch(imInstitutionPicked({ institution, track }));
        goTo("im_discipline");
    };

    const handleSelectImDiscipline = (discipline: ImDiscipline) => {
        dispatch(imDisciplinePicked(discipline));
        goTo("im_specialty");
    };

    const handleSelectCollege = (college: College) => {
        if (!selectedUniversityId) return;
        const goesToSpecialty = hasSelectableSpecialties(selectedUniversityId, college.key, selectedGender ?? undefined, selectedTrack);
        dispatch(collegePicked({
            college,
            requiredCount: goesToSpecialty
                ? null
                : getRequiredFields(selectedUniversityId, college.weights).length,
        }));
        goTo(goesToSpecialty ? "specialty" : "scores");
    };

    const handleSelectSpecialty = (department: Department) => {
        // For FM and IM, the picked specialty also determines selectedCollege
        // (the weight template lives on the college). Look up the matching
        // college from the active section's data.
        const matchedCollege = (isFm || isIm)
            ? collegesList.find((c) => c.key === department.collegeKey)
            : undefined;
        const weightsCollege = matchedCollege ?? selectedCollege;
        dispatch(specialtyPicked({
            department,
            college: matchedCollege,
            requiredCount: selectedUniversityId && weightsCollege
                ? getRequiredFields(selectedUniversityId, department.weightsOverride ?? weightsCollege.weights).length
                : 0,
        }));
        goTo("scores");
    };

    const handleResult = (newScores: Scores, rate: number) => {
        dispatch(resultReady({ scores: newScores, rate }));
        goTo("results");
    };

    const collegesList = selectedUniversityId
        ? getColleges(selectedUniversityId, selectedGender ?? undefined)
        : [];

    const effectiveCollege = useMemo(() => {
        if (!selectedCollege || !selectedDepartment) return selectedCollege;
        if (selectedDepartment.collegeKey === selectedCollege.key) return selectedCollege;
        return collegesList.find((c) => c.key === selectedDepartment.collegeKey) ?? selectedCollege;
    }, [selectedCollege, selectedDepartment, collegesList]);

    return (
        <div ref={sectionRef} id="dream-university-wizard" className="relative py-12 sm:py-8 bg-white rounded-3xl flex-1 w-full flex flex-col items-center overflow-hidden">
            {/* Desktop: floats in the empty side space beside the header, aligned with the title row */}
            <div className="pointer-events-none absolute inset-x-0 top-[104px] z-20 hidden justify-end px-6 lg:flex">
                <WhatsAppContact />
            </div>

            <div className="w-full max-w-2xl px-4 sm:px-6">
                <StepIndicator
                    steps={steps}
                    currentStep={currentStepIndex}
                />

                {/* Mobile/tablet: in-flow below the stepper, just above the title
                    (no side space to float into, and clear of the sticky navbar) */}
                <div className="mt-5 mb-1 flex justify-end lg:hidden">
                    <WhatsAppContact />
                </div>

                <AnimatePresence mode="wait">
                    {activeStepId === "university" && (
                        <motion.div key="university" variants={stepVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.2 }}>
                            <ChooseUniversity onSelectUniversity={handleSelectUniversity} />
                        </motion.div>
                    )}

                    {activeStepId === "gender" && selectedUniversityId && (
                        <motion.div key="gender" variants={stepVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.2 }}>
                            <ChooseGender
                                universityNameKey={universityInfo?.nameKey || ""}
                                onBack={() => goTo("university")}
                                onSelectGender={handleSelectGender}
                            />
                        </motion.div>
                    )}

                    {activeStepId === "paaet_docs" && isPaaet && selectedGender && (
                        <motion.div key="paaet_docs" variants={stepVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.2 }}>
                            <PaaetDocs
                                gender={selectedGender}
                                universityNameKey={universityInfo?.nameKey || ""}
                                onBack={() => goTo("gender")}
                            />
                        </motion.div>
                    )}

                    {activeStepId === "fm_country" && isFm && (
                        <motion.div key="fm_country" variants={stepVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.2 }}>
                            <ChooseFmCountry
                                universityNameKey={universityInfo?.nameKey || ""}
                                initialTrack={selectedTrack}
                                onBack={() => goTo("university")}
                                onSelectCountry={handleSelectFmCountry}
                                onTrackChange={(track) => dispatch(trackChanged(track))}
                            />
                        </motion.div>
                    )}

                    {activeStepId === "fm_college" && isFm && fmCountry && (
                        <motion.div key="fm_college" variants={stepVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.2 }}>
                            <ChooseFmCollege
                                universityNameKey={universityInfo?.nameKey || ""}
                                country={fmCountry}
                                track={selectedTrack ?? "scientific"}
                                onBack={() => goTo("fm_country")}
                                onSelectDiscipline={handleSelectFmDiscipline}
                            />
                        </motion.div>
                    )}

                    {activeStepId === "im_institution" && isIm && (
                        <motion.div key="im_institution" variants={stepVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.2 }}>
                            <ChooseImInstitution
                                universityNameKey={universityInfo?.nameKey || ""}
                                initialTrack={selectedTrack}
                                onBack={() => goTo("university")}
                                onSelectInstitution={handleSelectImInstitution}
                                onTrackChange={(track) => dispatch(trackChanged(track))}
                            />
                        </motion.div>
                    )}

                    {activeStepId === "im_discipline" && isIm && imInstitution && (
                        <motion.div key="im_discipline" variants={stepVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.2 }}>
                            <ChooseImDiscipline
                                universityNameKey={universityInfo?.nameKey || ""}
                                institution={imInstitution}
                                track={selectedTrack ?? "scientific"}
                                onBack={() => goTo("im_institution")}
                                onSelectDiscipline={handleSelectImDiscipline}
                            />
                        </motion.div>
                    )}

                    {activeStepId === "im_specialty" && isIm && imInstitution && imDiscipline && (
                        <motion.div key="im_specialty" variants={stepVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.2 }}>
                            <ChooseImSpecialty
                                universityNameKey={universityInfo?.nameKey || ""}
                                institution={imInstitution}
                                discipline={imDiscipline}
                                track={selectedTrack ?? "scientific"}
                                onBack={() => goTo("im_discipline")}
                                onSelectSpecialty={handleSelectSpecialty}
                            />
                        </motion.div>
                    )}

                    {activeStepId === "college" && selectedUniversityId && !isFm && !isIm && (
                        <motion.div key="college" variants={stepVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.2 }}>
                            <ChooseCollege
                                colleges={collegesList}
                                universityId={selectedUniversityId!}
                                universityNameKey={universityInfo?.nameKey || ""}
                                initialTrack={selectedTrack}
                                onBack={() => goTo(needsGender ? "gender" : "university")}
                                onSelectCollege={handleSelectCollege}
                                onTrackChange={(track) => dispatch(trackChanged(track))}
                            />
                        </motion.div>
                    )}

                    {activeStepId === "specialty" && selectedUniversityId && isFm && fmCountry && fmDiscipline && (
                        <motion.div key="specialty" variants={stepVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.2 }}>
                            <ChooseFmSpecialty
                                universityNameKey={universityInfo?.nameKey || ""}
                                country={fmCountry}
                                discipline={fmDiscipline}
                                track={selectedTrack ?? "scientific"}
                                onBack={() => goTo("fm_college")}
                                onSelectSpecialty={handleSelectSpecialty}
                            />
                        </motion.div>
                    )}

                    {activeStepId === "specialty" && selectedUniversityId && !isFm && !isIm && selectedCollege && (
                        <motion.div key="specialty" variants={stepVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.2 }}>
                            <ChooseSpecialty
                                universityId={selectedUniversityId}
                                college={selectedCollege}
                                selectedTrack={selectedTrack}
                                selectedGender={selectedGender}
                                onBack={() => goTo("college")}
                                onSelectSpecialty={handleSelectSpecialty}
                            />
                        </motion.div>
                    )}

                    {activeStepId === "scores" && selectedUniversityId && (effectiveCollege || selectedCollege) && (
                        <motion.div key="scores" variants={stepVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.2 }}>
                            <ScoreCalculator
                                universityId={selectedUniversityId}
                                college={(effectiveCollege ?? selectedCollege) as College}
                                selectedDepartment={selectedDepartment}
                                selectedTrack={selectedTrack}
                                selectedGender={selectedGender}
                                onBack={() => goTo(
                                    isFm ? "specialty"
                                    : isIm ? "im_specialty"
                                    : (collegeHasSpecialties ? "specialty" : "college")
                                )}
                                onResult={handleResult}
                            />
                        </motion.div>
                    )}

                    {activeStepId === "results" && selectedUniversityId && (effectiveCollege || selectedCollege) && (
                        <motion.div key="results" variants={stepVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.2 }}>
                            <ResultsView
                                universityId={selectedUniversityId}
                                college={(effectiveCollege ?? selectedCollege) as College}
                                selectedDepartment={selectedDepartment}
                                selectedTrack={selectedTrack}
                                scores={scores}
                                equivalentRate={equivalentRate}
                                onBack={() => goTo("scores")}
                                onShowQualified={() => setShowQualifiedModal(true)}
                            />
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {showQualifiedModal && selectedUniversityId && (
                <QualifiedCollegesModal
                    universityId={selectedUniversityId}
                    scores={scores}
                    selectedTrack={selectedTrack}
                    selectedGender={selectedGender}
                    selectedCollegeKey={(effectiveCollege ?? selectedCollege)?.key ?? null}
                    selectedDepartment={selectedDepartment}
                    onClose={() => setShowQualifiedModal(false)}
                />
            )}
        </div>
    );
}
